/**
 * aiChatInit · 团队数据预热接口（POST /web/workgroup/folder/ai/chat/init）
 *
 * 协议语义重定位 (2026-05 重构):
 *   · 仅传 group_id, 不再传 conversation_id (接口不再有"创建/恢复会话"副作用)
 *   · 后端只做一件事: 同步调 photosearch RPC 拉半年内 500 张水印照片做聚合 →
 *     缓存进程内 4h → 后续 /send 复用同一份缓存, 省掉首条消息的 cold-build 延迟 (2-5s)
 *   · 调用时机: 进入页面 1 次 + 切换团队时再 1 次
 *
 * 前端拿到的 aggregated.recommended / .top_contents 可以渲染首页"推荐分类"卡片;
 * note 是给前端首页用的人话总结 (welcome 文案);
 * recommended_prompts 是 QuickPrompts 直接发送的完整文案。
 *
 * 新会话由 POST /send 接口在用户发首条消息时按需创建并通过 SSE done 事件返回 id,
 * 会话恢复由 POST /history 拉历史消息单独完成, 两者都跟本接口完全解耦。
 *
 * 接口文档: 钉钉 QG53mjyd80R04wE4HgYEnN3RV6zbX04v §1
 */

declare const __API_SERVER_HOST__: string

/** 单个水印字段下的 top content 项（如 "项目1" 出现 32 次，含 3 张样本 photo seq） */
export interface AggregatedTopContent {
  content: string
  count: number
  samples: number[]
}

/** 一个水印字段（title）下的分布概况 */
export interface AggregatedTitleDistribution {
  distinct_count: number
  coverage: number
  top_contents: AggregatedTopContent[]
}

/** 启动注入聚合数据 */
export interface AggregatedWatermarkData {
  /** 团队所有水印字段名（["项目","施工单位",...]） */
  all_titles: string[] | null
  /** 各字段下的 top 5 contents */
  title_distribution: Record<string, AggregatedTitleDistribution> | null
  /** AI 推荐分类维度顺序（首页主推） */
  recommended: string[] | null
  /** 实际采样到的照片数（团队没数据时为 0） */
  sampled_count: number
  built_at: string
}

/**
 * 推荐分类项 · 后端原始 (snake_case) 形态
 *
 * 前端在 prewarmTeam 解析时统一转 camelCase 的 RecommendedPrompt（见 utils/recommendedPrompt.ts），
 * 渲染层（EmptyState / QuickPrompts）只看 camelCase 不接触 snake_case。
 */
export interface RawRecommendedPrompt {
  /** 用户点击后发给 AI 的完整 prompt 文案 */
  text: string
  /** 语义 icon 类型 · 前端 type → AntD icon 映射后渲染 */
  icon_type: string
  /** 卡片副标题（说明 AI 提取来源，如「提取拍摄时间」） */
  subtitle: string
}

export interface AiChatInitResponse {
  aggregated: AggregatedWatermarkData
  /** 给前端首页用的人话总结，可直接当 AI 第一条欢迎语展示 */
  note: string
  /**
   * EmptyState / QuickPrompts 渲染的推荐分类项列表。
   *
   * 后端按 aggregated.recommended（基于 500 张实拍 coverage*distinct 算分排序）拼好
   * 含 text/icon_type/subtitle，前端**不再做模板拼接**——避免 UI 词汇变动时多处改文案。
   * 空数组时前端走 FALLBACK_RECOMMENDED_PROMPTS 兜底。
   */
  recommended_prompts: RawRecommendedPrompt[]
}

export interface AiChatInitOptions {
  /** 团队 UUID（group_id_real） · 必填 */
  groupID: string
  signal?: AbortSignal
}

/**
 * 调一次 /web/workgroup/folder/ai/chat/init · 仅做团队级数据预热
 *
 * 失败 (网络/接口错) 时抛 Error; 调用方可降级 fallback (welcome 文案走 FALLBACK_HEAD,
 * 不影响发消息 — 用户在欢迎页发首条消息走 /send conversation_id=空 后端会兜底)。
 */
export async function fetchAiChatInit(
  options: AiChatInitOptions
): Promise<AiChatInitResponse> {
  // POST + JSON body (与 webxcameraapi 内部 /web/folder/* 风格一致)
  const url = __API_SERVER_HOST__ + '/web/workgroup/folder/ai/chat/init'
  const body: Record<string, string> = { group_id: options.groupID }

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: options.signal
  })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`)
  }
  const json = await res.json()
  return json && json.data !== undefined ? json.data : json
}
